import Feature from "ol/Feature";
import { SPACECRAFT_METADATA } from "./common";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { LngLat, getAverageOrbitTimeMS, getInclination, getLastAntemeridianCrossingTimeMS, getLatLngObj, getOrbitTrackSync, getSatelliteInfo, getSatelliteName, parseTLE } from "tle.js";
import { LineString, Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Icon from "ol/style/Icon";
import Overlay from "ol/Overlay";

export class MapSpacecraft {
  private TLE: [string, string, string]
  private TRACK_COLOR: string

  constructor(TLE: [string, string, string], trackColor: string) {
    this.TLE = TLE,
    this.TRACK_COLOR = trackColor
  }

  get info() {
    return {
      ...getSatelliteInfo(this.TLE, Date.now()),
      inclination: getInclination(this.TLE)
    }
  }

  get name() {
    return getSatelliteName(this.TLE)
  }

  get location() {
    let currentLocation = getLatLngObj(this.TLE)
    return currentLocation ? fromLonLat([currentLocation.lng, currentLocation.lat]) : [0, 0]
  }

  get locationDeg() {
    let currentLocation = getLatLngObj(this.TLE)
    return currentLocation ? [currentLocation.lng, currentLocation.lat] : [0, 0]
  }

  get height() {
    return this.info.height
  }

  getLayer(): VectorLayer<VectorSource> {
    let spacecraft_metadata = SPACECRAFT_METADATA.find((m) => this.name.match(m.match)) || {
      match: /.*/,
      class: "Error",
      icon: {
        name: "error.png",
        scale: 0.16,
      }
    }
    let spacecraftMarker = new Feature({
      id: this.name,
      geometry: new Point(this.location),
      attributes: {
        name: this.name,
      }
    })
    spacecraftMarker.setStyle(new Style({
      image: new Icon({
        src: `img/icons/issmccmap/${spacecraft_metadata.icon.name}`,
        scale: spacecraft_metadata.icon.scale,
      })
    }))

    let times = new Array<number>()
    let ts = new Date()
    while (times.length < 3 && !(ts.getTime() > (Date.now() + 12 * getAverageOrbitTimeMS(this.TLE)))) {
      let ct = getLastAntemeridianCrossingTimeMS(parseTLE(this.TLE), ts.getTime())
      if (!times.find(t => ct - t < 1000)) {
        times.push(ct)
      }
      ts.setTime(ts.getTime() + getAverageOrbitTimeMS(this.TLE))
    }

    return new VectorLayer({
        source: new VectorSource<LineString|Point>({
          features: [
              ...times.map(
                ts => {
                  return getOrbitTrackSync({tle: this.TLE, startTimeMS: ts, stepMS: 2 * 1000, isLngLatFormat: true})
                }
              )
              .map((groundtrack: Array<LngLat>) => {
                return groundtrack.map((point: LngLat) => {
                  return [point[0], point[1]] as [number, number]
                })
              })
              .map((gt: Array<[number, number]>) => {
                let feature = new Feature({
                  geometry: new LineString(gt.map((p: [number, number]) => fromLonLat(p)))
                });
                feature.setStyle(new Style({
                  stroke: new Stroke({
                    color: this.TRACK_COLOR,
                    width: 1.5,
                  })}))
                return feature
              }),
              spacecraftMarker
            ]
        }),
        zIndex: 20
      })
  }

  getPopup(map: Element, offset?: {x?: number, y?: number}): Overlay {
    let popupEl = document.createElement("div");
    popupEl.classList.add("ol-popup");
    let popupTitleEl = document.createElement("div");
    popupTitleEl.classList.add("popup-title");
    popupTitleEl.innerText = this.name;
    popupEl.appendChild(popupTitleEl);
    let popupBodyEl = document.createElement("div");
    popupBodyEl.classList.add("popup-body");
    popupBodyEl.innerText = `LAT=${this.locationDeg[1].toFixed(1)}
      ALT=${this.height.toFixed(1)}
      LON=${this.locationDeg[0].toFixed(1)}
      INC=${this.info.inclination.toFixed(1)}
    `;
    popupEl.appendChild(popupBodyEl);

    return new Overlay({
      position: [this.location[0] + (offset?.x || 0), this.location[1] + (offset?.y || 0)],
      element: popupEl
    })
  }
}